import _ from "lodash";
import React from "react";

import { getMemoizedLangFunction } from "Utils";
import ACLStorage from "lib/aclService";
import ACLStore from "lib/aclStore";
import { ROUTES } from "./Module";
import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";
import { SCHOOL_LIBRARY_VIEW_PERM } from "Constants/permissionConstants";
import { LeLibraryOutlined, UnitPlanOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import classes from "../components/SchoolLibrary.scss";
import {
  UNIT_TYPE_INTERDISCIPLINARY,
  UNIT_TYPE_SUBJECT_SPECIFIC,
} from "Teacher/routes/Courses/routes/Course/Constants/StringConstants";
import { getIsCombineCurriculumHomePageEnabled } from "Platform/modules/PlatformUtils";
import { getIsCourseBasedGradingEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { CURRICULUM_TYPE_ORDER_MAPPING } from "Constants";

const getTabOptions = ({ t, curriculumType }) => {
  const leLibraryLabel =
    curriculumType === CURRICULUM_TYPE_PYP
      ? t("common:le_label_plural")
      : t("common:le_slash_assessments");

  return [
    { key: "root", children: [ROUTES.UNIT_LIBRARY, ROUTES.LE_LIBRARY] },
    {
      key: ROUTES.UNIT_LIBRARY,
      title: t("common:unit_plural"),
      tier: 1,
      prefix: <UnitPlanOutlined />,
      suffix: (
        <div
          className={classNames(classes.shortcutKey, "text-body-3")}
          dangerouslySetInnerHTML={{ __html: "1" }}
        />
      ),
      parent: "root",
      isCascadingMenu: false,
      children: [],
    },
    {
      key: ROUTES.LE_LIBRARY,
      title: leLibraryLabel,
      tier: 1,
      prefix: <LeLibraryOutlined />,
      suffix: (
        <div
          className={classNames(classes.shortcutKey, "text-body-3")}
          dangerouslySetInnerHTML={{ __html: "2" }}
        />
      ),
      parent: "root",
      isCascadingMenu: false,
      children: [],
    },
  ];
};

export const getTabOptionsMemoized = getMemoizedLangFunction(getTabOptions);

export const isSchoolLibraryEnabled = () =>
  ACLStorage.getModuleSettingsValue(SCHOOL_LIBRARY_VIEW_PERM);

export const isSchoolLibraryRoute = () => {
  const url = window.location.href;
  const urlSegments = url.split("/");
  return _.includes(urlSegments, "school-library");
};

const getFormattedFilterOptions = ({
  data,
  labelField = "title",
  valueField = "id",
  getLabel,
  alwaysGroup = false,
  showSubLabelField = false,
  subLabelField = "",
}) => {
  return getDataGroupedByCurriculum({
    data,
    valueField,
    labelField,
    generateSortedData: true,
    getLabel,
    alwaysGroup,
    showSubLabelField,
    subLabelField,
  });
};

const getDataGroupedByCurriculum = ({
  data = [],
  valueField = "id",
  labelField = "name",
  generateSortedData = false,
  getLabel,
  alwaysGroup = false,
  showSubLabelField,
  subLabelField,
}) => {
  const resolveLabel = item => {
    if (_.isFunction(getLabel)) {
      return getLabel(item);
    }
    return _.get(item, labelField);
  };

  const groupedByCurriculum = _.groupBy(data, item =>
    _.get(item, "curriculumProgram.id")
  );

  if (_.size(groupedByCurriculum) <= 1 && !alwaysGroup) {
    return _.map(data, item => {
      return {
        label: resolveLabel(item),
        value: item[valueField],
        ...(showSubLabelField &&
          !_.isEmpty(subLabelField) && {
            subLabel: _.get(item, subLabelField),
          }),
      };
    });
  }

  const curriculumPrograms = [];
  _.forEach(groupedByCurriculum, (data, curriculumId) => {
    const firstItem = _.first(data);
    curriculumPrograms.push({
      id: curriculumId,
      acronym: _.get(firstItem, "curriculumProgram.acronym"),
      type: _.get(firstItem, "curriculumProgram.type"),
      options: data,
    });
  });

  let programsToProcess = curriculumPrograms;
  if (generateSortedData) {
    programsToProcess = _.orderBy(
      curriculumPrograms,
      [program => CURRICULUM_TYPE_ORDER_MAPPING[program.type]],
      ["asc"]
    );
  }

  const formattedData = [];
  _.forEach(programsToProcess, program => {
    formattedData.push({
      label: program.acronym,
      isSectionHeader: true,
      curriculumProgramId: program.id,
    });

    _.forEach(program.options, item => {
      formattedData.push({
        label: resolveLabel(item),
        ...(showSubLabelField &&
          !_.isEmpty(subLabelField) && {
            subLabel: _.get(item, subLabelField),
          }),
        value: item[valueField],
        curriculumProgramId: program.id,
      });
    });
  });

  return formattedData;
};

export const getFilterOptions = ({
  t,
  filters,
  gradeOptions,
  subjectOptions,
  schoolOptions,
  courseOptions,
  classOptions,
  postTypeOptions = [],
  showUnitTypeFilter = false,
  creatorOptions = [],
  academicYearOptions = [],
  isSchoolOptionsEnabled = true,
  isPYP = false,
}) => {
  const options = {};
  const defaultSelectedOptions = {};

  const populateDefaultSelectedOptions = ({ filterName, filterOptions }) => {
    const filtersValue = _.get(filters, filterName, []);
    if (filtersValue === "ALL") {
      defaultSelectedOptions[filterName] = _.map(
        _.filter(filterOptions, option => !option.isSectionHeader),
        "value"
      );
    } else {
      defaultSelectedOptions[filterName] = filtersValue;
    }
  };

  const isCombineCurriculumHomePageEnabled = getIsCombineCurriculumHomePageEnabled();
  if (isCombineCurriculumHomePageEnabled && isSchoolOptionsEnabled) {
    options["curriculumProgramIds"] = {
      options: schoolOptions,
      title: t("common:school_plural"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:school_plural"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "curriculumProgramIds",
      filterOptions: schoolOptions,
    });
  }

  const unitTypeOptions = [
    {
      label:
        isPYP || isSchoolOptionsEnabled
          ? t("common:transdisciplinary")
          : t("common:interdisciplinary"),
      value: UNIT_TYPE_INTERDISCIPLINARY,
      key: UNIT_TYPE_INTERDISCIPLINARY,
    },
    {
      label: t("common:subject_specific"),
      value: UNIT_TYPE_SUBJECT_SPECIFIC,
      key: UNIT_TYPE_SUBJECT_SPECIFIC,
    },
  ];

  // Grade
  if (!_.isEmpty(gradeOptions)) {
    options["grades"] = {
      options: gradeOptions,
      title: t("common:grade"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:grade_plural"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "grades",
      filterOptions: gradeOptions,
    });
  }

  // postTypeOptions
  if (!_.isEmpty(postTypeOptions)) {
    options["types"] = {
      options: postTypeOptions,
      title: t("common:assignment_type"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:types"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "types",
      filterOptions: postTypeOptions,
    });
  }

  // Subject
  if (!_.isEmpty(subjectOptions)) {
    options["subjects"] = {
      options: subjectOptions,
      title: t("common:subject"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:subject_plural"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "subjects",
      filterOptions: subjectOptions,
    });
  }

  // Learning Courses
  if (!_.isEmpty(courseOptions)) {
    options["learningCourses"] = {
      options: courseOptions,
      title: t("common:courses"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:learning_course_lowercase_plural"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "learningCourses",
      filterOptions: courseOptions,
    });
  }

  // classes
  if (!_.isEmpty(classOptions)) {
    options["classes"] = {
      options: classOptions,
      title: t("common:class_plural"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:class_lowercase_other"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "classes",
      filterOptions: classOptions,
    });
  }

  if (!_.isEmpty(creatorOptions)) {
    //creators
    options["createdBy"] = {
      options: creatorOptions,
      title: t("common:creator"),
      allText: t("common:select_all"),
    };
    populateDefaultSelectedOptions({
      filterName: "createdBy",
      filterOptions: creatorOptions,
    });
  }

  if (!_.isEmpty(academicYearOptions)) {
    //academic Years
    options["academicYears"] = {
      options: academicYearOptions,
      title: t("common:academic_year"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:academic_year_plural"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "academicYears",
      filterOptions: academicYearOptions,
    });
  }

  if (showUnitTypeFilter) {
    //unitTypeOptions
    options["unitType"] = {
      options: unitTypeOptions,
      title: t("common:unit_type_label"),
      allText: t("common:select_all"),
    };
    populateDefaultSelectedOptions({
      filterName: "unitType",
      filterOptions: unitTypeOptions,
    });
  }

  return { options, defaultSelectedOptions };
};

export const getVisibleFilterOptionsMemoized = _.memoize(
  ({
    gradesData,
    schoolsData,
    postTypesData,
    subjectsData,
    courseData,
    classData = [],
    creatorList,
    academicYearsList,
    isLearningCoursesEnabled,
    isAYWisePermsSetupEnabled,
    isCombinedFiltersEnabled,
    isSchoolOptionsEnabled,
  }) => {
    const formattedClassData = isCombinedFiltersEnabled
      ? classData.map(classOption => ({
          ...classOption,
          academicYearsString: _.reduce(
            _.get(classOption, "academicYears", []),
            (yearsString, academicYear) => {
              const label = _.get(academicYear, "label", "");
              return label
                ? yearsString
                  ? yearsString + ", " + label
                  : label
                : yearsString;
            },
            ""
          ),
        }))
      : classData;
    const config = {
      grades: {
        data: gradesData,
        optionsKey: "gradeOptions",
        shouldProcess: true,
        labelField: "name",
        alwaysGroup: isCombinedFiltersEnabled,
        isHidden: false,
      },

      schools: {
        data: schoolsData,
        optionsKey: "schoolOptions",
        shouldProcess: true,
        labelField: "acronym",
        isHidden: !isSchoolOptionsEnabled,
      },

      types: {
        data: postTypesData,
        optionsKey: "postTypeOptions",
        shouldProcess: true,
        labelField: "label",
        isHidden: false,
      },

      subjects: {
        data: subjectsData,
        optionsKey: "subjectOptions",
        shouldProcess: true,
        labelField: "name",
        alwaysGroup: isCombinedFiltersEnabled,
        isHidden: getIsCourseBasedGradingEnabled(),
      },

      courses: {
        data: courseData,
        optionsKey: "courseOptions",
        shouldProcess: true,
        labelField: "title",
        alwaysGroup: isCombinedFiltersEnabled,
        isHidden: !isLearningCoursesEnabled,
      },

      classes: {
        data: formattedClassData,
        optionsKey: "classOptions",
        shouldProcess: true,
        labelField: "title",
        isHidden: isLearningCoursesEnabled
          ? !isAYWisePermsSetupEnabled ||
            !ACLStore.can("FeatureFlag:ClassFilterInSchoolLibrary")
          : !ACLStore.can("FeatureFlag:ClassFilterInSchoolLibrary"),
      },

      creators: {
        data: creatorList,
        optionsKey: "creatorOptions",
        shouldProcess: false,
        label: "Creators",
        isHidden: false,
      },

      academicYears: {
        data: academicYearsList,
        optionsKey: "academicYearOptions",
        shouldProcess: false,
        label: "Academic Years",
        isHidden: false,
      },
    };

    return _.reduce(
      config,
      (result, item) => {
        const {
          data,
          optionsKey,
          shouldProcess,
          labelField,
          alwaysGroup,
          isHidden,
        } = item;

        if (isHidden) {
          result[optionsKey] = [];
          return result;
        }

        if (shouldProcess) {
          result[optionsKey] = getFormattedFilterOptions({
            data,
            labelField,
            alwaysGroup,
            ...(isCombinedFiltersEnabled &&
              optionsKey === "classOptions" && {
                showSubLabelField: true,
                subLabelField: "academicYearsString",
              }),
          });
        } else {
          result[optionsKey] = data;
        }

        return result;
      },
      {}
    );
  },
  params => JSON.stringify(params)
);

const _getFilteredGrades = ({
  organizationDetails,
  curriculumProgramIds = [],
}) => {
  return _.filter(organizationDetails?.grades, grade =>
    _.includes(curriculumProgramIds, _.get(grade, "curriculumProgram.id", ""))
  );
};

export const getFilteredGrades = _.memoize(
  _getFilteredGrades,
  ({ organizationDetails, curriculumProgramIds }) =>
    JSON.stringify({
      orgCpIds: _.map(organizationDetails?.grades, "curriculumProgram.id"),
      cpIds: curriculumProgramIds,
    })
);

export const getIsCombinedFiltersEnabledInSchoolLibrary = () => {
  return ACLStore.can("FeatureFlag:EnableCombineFilterInSchoolLibrary");
};

export const getShouldAddSchoolFilterInSchoolLibrary = () => {
  return ACLStore.can("FeatureFlag:AddSchoolFilterInSchoolLibrary");
};
